import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = n => n.internalid;

const memberNotesSlice = createSlice({
    name: 'memberNotes',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            const byMember = groupBy(ensureArray(action.payload.data), 'memberid')
            ensureArray(action.payload.ids).forEach(memberid => {
                if (byMember[memberid]) {
                    state.data[memberid] = byMember[memberid];
                } else {
                    delete state.data[memberid];
                }
            })
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byMember = groupBy(toUpdate, 'memberid');
            Object.entries(byMember).forEach(([ memberid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[memberid] = (state.data[memberid]?.filter(n => !ids.has(key(n))) ?? []).concat(notes);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byMember = groupBy(toRemove, 'memberid');
            Object.entries(byMember).forEach(([memberid, notes]) => {
                const toRemove = new Set(notes.map(n => key(n)));
                state.data[memberid] = state.data[memberid]?.filter(n => !toRemove.has(key(n)));
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberNotesSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffNotes', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffNotes');

// TODO add multi-fetch
const fetchMemberNotes = memberid => network => network.get('/api/v1/membernote/' + encodeURIComponent(memberid), data => memberNotesSlice.actions.set({ data, ids: [memberid] }), visualizeFailure('get'));
export const addMemberNote = (network, data) => network.post('/api/v1/membernote', data, memberNotesSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectAllMemberNoteData = state => state.memberNotes.data;
const selectMemberNotes = memberid => createSelector([selectAllMemberNoteData], notes => notes[memberid] ?? []);
// #endregion

const listen = {
    memberNoteCreated: (note, { dispatch, websocket }) => {
        markFetched(websocket, 'member_note', key(note))
        dispatch(memberNotesSlice.actions.addOrUpdate(note))
    }
}

const memberInitNotesByID = initMapFactory(fetchMemberNotes, listen);
registerEntityBasedFetch('member', memberInitNotesByID);



const cleanup = { root: memberNotesSlice.name, all: state => Object.values(selectAllMemberNoteData(state)).flat(), removeAll: memberNotesSlice.actions.remove, key };
export const [useMemberNotes] = dataHookFactory(selectMemberNotes, memberInitNotesByID, { cleanup });

const fullRoles = ['administrator', 'training manager', 'member manager'];
export const canUpdateMemberNotes = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);

const checkPermissions = (credentials, member, requiredRoles, condition) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false && !member?.inactive) ?? false 
}
const reducer = { name: memberNotesSlice.name, reducer: memberNotesSlice.reducer };
export default reducer;